import React, { FC, useMemo } from 'react';
import { useTheme } from 'styled-components';
import { TimeSeriesAreaChartRecharts } from '../../../../shared/charts/recharts';
import {
  buildValueLabelRow,
  formatRainProbabilityChartLabel,
} from '../../helpers/graph/graphData';
import {
  WEATHER_CHART_CHROME_COLORS,
  WEATHER_CHART_PLOT_INTERACTION_PROPS,
  WEATHER_CHART_PLOT_HEIGHT,
  WEATHER_CHART_SHOW_BACKGROUND_GRADIENT,
  buildWeatherChartBands,
  createWeatherChartXTickFormatter,
  type WeatherChartBaseProps,
} from './chartDefaults';
import WeatherTheme from './weather-theme';

export type RainChartProps = WeatherChartBaseProps;

const RainChart: FC<RainChartProps> = ({
  data,
  domain,
  timeZone,
  selectedTime,
  defaultSelectedTime,
  onSelectedTimeChange,
  onScrubStart,
  onScrubEnd,
}) => {
  const theme = useTheme();

  const backgroundBands = useMemo(
    () => buildWeatherChartBands(domain, timeZone),
    [domain, timeZone],
  );

  const labelRows = useMemo(
    () => [
      buildValueLabelRow(data, formatRainProbabilityChartLabel, {
        color: WeatherTheme.colors.rain,
      }),
    ],
    [data],
  );

  const formatXTick = useMemo(
    () => createWeatherChartXTickFormatter(timeZone),
    [timeZone],
  );

  return (
    <TimeSeriesAreaChartRecharts
      data={data}
      domain={domain}
      timeZone={timeZone}
      colors={{
        line: WeatherTheme.colors.rain,
        fill: {
          top: WeatherTheme.colors.rainfill,
          bottom: WeatherTheme.colors.rainfill,
        },
        scrubberDot: WeatherTheme.colors.rain,
      }}
      plotHeight={WEATHER_CHART_PLOT_HEIGHT}
      xTickIntervalHours={3}
      hideMidnightTicks
      formatXTick={formatXTick}
      xTickColor={theme.colors.darkergrey}
      backgroundBands={backgroundBands}
      showBackgroundGradient={WEATHER_CHART_SHOW_BACKGROUND_GRADIENT}
      labelRows={labelRows}
      yDomain={[0, 100]}
      selectedTime={selectedTime}
      defaultSelectedTime={defaultSelectedTime}
      onSelectedTimeChange={onSelectedTimeChange}
      onScrubStart={onScrubStart}
      onScrubEnd={onScrubEnd}
      plotInteractionProps={WEATHER_CHART_PLOT_INTERACTION_PROPS}
      chromeColors={WEATHER_CHART_CHROME_COLORS}
    />
  );
};

export default RainChart;
